import * as React from 'react';
import {Link as RoutedLink} from 'react-router-dom';

import {LinkCard} from 'shells/card/link-card';
import {FileUploadModalPage} from 'shells/form/file-upload-modal-page';
import {CsvImportIcon} from 'shells/icon/svg/csv-import-icon';
import {Stack} from 'shells/layout/stack/stack';
import {Link} from 'shells/link/link';
import {Modal} from 'shells/modal/modal';
import {Body} from 'shells/typography/body';

import {OpenableModals, useOpenableModal} from '../../../openable-modals';

import {analyticsClient} from '../../../../analytics-client';

import {Home, AnotherCrmPage} from './pages';

import styles from './import-modal.module.css';

export type Page = 'home' | 'csv' | 'anotherCrm';

const CSV_IMPORT_SUPPORT_ARTICLE_URL =
    'https://support.nutshell.com/en/articles/8429034-import-add-companies-people-and-leads-from-csv';

export function ImportModal() {
    const {isOpen, onClose} = useOpenableModal(OpenableModals.IMPORT_MODAL);
    const [page, setPage] = React.useState<Page>('home');

    const onSelectImportType = (type: Page) => {
        if (type === 'anotherCrm') {
            analyticsClient.track(analyticsClient.EVENTS.TRACK_IMPORT2_COMPETITORS_VIEWED);
        }

        setPage(type);
    };

    return (
        <Modal
            headerBackButton={page !== 'home'}
            onHeaderBackButtonPress={() => {
                setPage('home');
            }}
            headerText={getTitleTextForPage(page)}
            isOpen={isOpen}
            onClose={onClose}
            minHeight={570}
            maxHeight={570}
            minWidth={932}
            maxWidth={932}
        >
            <div className={styles.container}>
                <div className='mb-16'>
                    <Body size='large' color='medium'>
                        {getSubtitleMessage(page)}
                    </Body>
                </div>
                <div className={styles.content}>
                    {page === 'home' ? (
                        <Home onSelectImportType={onSelectImportType} onCloseModal={onClose} />
                    ) : undefined}
                    {page === 'csv' ? <FileUploadModalPage /> : undefined}
                    {page === 'anotherCrm' ? <AnotherCrmPage onCloseModal={onClose} /> : undefined}
                </div>
                {page === 'csv' && (
                    <div className={styles['template-csv-banner']}>
                        <LinkCard
                            href={CSV_IMPORT_SUPPORT_ARTICLE_URL}
                            preventUnderline={true}
                            newTab={true}
                        >
                            <Stack
                                spacing={0}
                                isHorizontal={true}
                                isFullWidth={true}
                                alignItems='center'
                            >
                                <div className='px-16 mt-2 mb-2'>
                                    <CsvImportIcon size={60} />
                                </div>
                                <Stack spacing={4}>
                                    <Body isBold={true}>Need a head start?</Body>
                                    <div>
                                        <Body>You can download an</Body>{' '}
                                        <Link
                                            href={CSV_IMPORT_SUPPORT_ARTICLE_URL}
                                            variant='primary'
                                            newTab={true}
                                        >
                                            import template spreadsheet.
                                        </Link>
                                    </div>
                                </Stack>
                            </Stack>
                        </LinkCard>
                    </div>
                )}
                <div className={styles.banner}>
                    <LinkCard
                        // @ts-expect-error
                        as={RoutedLink}
                        to='/setup/import/white-glove'
                        onClick={onClose}
                        preventUnderline={true}
                    >
                        <Stack
                            spacing={0}
                            isHorizontal={true}
                            isFullWidth={true}
                            alignItems='center'
                        >
                            <img
                                src='/include/images/import/icons/icon-white-glove_2x.png'
                                className={styles.image}
                            />
                            <div>
                                <Stack spacing={4}>
                                    <Body isBold={true}>
                                        Want a helping hand? We’ll do it for you!
                                    </Body>
                                    <div>
                                        <Body>
                                            Our friendly team is here to help. Send us a file of
                                            your data, and we’ll take care of the rest.
                                        </Body>{' '}
                                        <Link
                                            // @ts-expect-error
                                            as={RoutedLink}
                                            to='/setup/import/white-glove'
                                            variant='primary'
                                            onClick={onClose}
                                        >
                                            Get started
                                        </Link>
                                    </div>
                                </Stack>
                            </div>
                        </Stack>
                    </LinkCard>
                </div>
            </div>
        </Modal>
    );
}

function getTitleTextForPage(page: string) {
    switch (page) {
        case 'home':
            return 'Import your existing contacts';
        case 'csv':
            return 'Import from a CSV file';
        case 'anotherCrm':
            return 'Import from a different CRM';
        default:
            return 'This shouldn’t happen';
    }
}

function getSubtitleMessage(page: Page) {
    switch (page) {
        case 'home':
            return 'Where is your data coming from?';
        case 'csv':
            return 'Great! Upload your CSV file to get started.';
        case 'anotherCrm':
            return 'You’re in great hands. Which CRM are you coming from?';
        default:
            return 'Where is your data coming from?';
    }
}
